<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Driver Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.driver_name')"
                v-model="filterOptions.driver_name"
              />
              <!-- End:: Driver Name Input -->

              <!-- Start:: Distribution Point Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.distribution_point_name')"
                v-model="filterOptions.dp_name"
              />
              <!-- End:: Distribution Point Name Input -->

              <!-- Start:: Package Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.package_number')"
                v-model="filterOptions.package_number"
              />
              <!-- End:: Package Number Input -->

              <!-- Start:: Period Filter -->
              <base-select-input
                col="3"
                :optionsList="periodOptions"
                :placeholder="$t('PLACEHOLDERS.period_filter')"
                v-model="filterOptions.period"
              />
              <!-- End:: Period Filter -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.startDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.endDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.financial_reports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 mr-2 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>

          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.serial`]="{ index }">
          <div class="table_image_wrapper">
            <p>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Package Number -->
        <template v-slot:[`item.package_number`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.package_number">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.package_number }}</p>
          </div>
        </template>
        <!-- End:: Package Number -->

        <!-- Start:: Driver Name -->
        <template v-slot:[`item.driver_name`]="{ item }">
          <div class="table_text_wrapper">
            <h6 class="text-danger" v-if="!item.driver_name">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>{{ item.driver_name }}</p>
          </div>
        </template>
        <!-- End:: Driver Name -->

        <!-- Start:: Distribution Point Name -->
        <template v-slot:[`item.dp_name`]="{ item }">
          <div class="table_text_wrapper">
            <p
              class="blue-grey--text text--darken-1"
              v-if="item.dp_name === '-'"
            >
              -
            </p>
            <p v-else>{{ item.dp_name }}</p>
          </div>
        </template>
        <!-- End:: Distribution Point Name -->

        <!-- Start:: Delivery Fee -->
        <template v-slot:[`item.delivery_fee`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.delivery_fee || 0 }}</p>
          </div>
        </template>
        <!-- End:: Delivery Fee -->

        <!-- Start:: Driver Profit -->
        <template v-slot:[`item.driver_profit`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.driver_profit || 0 }}</p>
          </div>
        </template>
        <!-- End:: Driver Profit -->

        <!-- Start:: Distribution Point Profit -->
        <template v-slot:[`item.dp_profit`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.dp_profit || 0 }}</p>
          </div>
        </template>
        <!-- End:: Distribution Point Profit -->

        <!-- Start:: System Profit -->
        <template v-slot:[`item.system_profit`]="{ item }">
          <div class="table_text_wrapper">
            <p>{{ item.system_profit || 0 }}</p>
          </div>
        </template>
        <!-- End:: System Profit -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: pdf Modal -->
          <!-- <v-dialog v-model="dialogPdf">
            <v-card>
              <button class="ex-btn-s" @click="clickedDownload"> {{ $t("PLACEHOLDERS.export_pdf") }}</button>

              <v-card-actions>
                <v-btn class="modal_cancel_btn" @click="dialogPdf = false">{{ $t("BUTTONS.cancel") }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog> -->
          <!-- End:: pdf Modal -->

          <!-- Start:: excel Modal -->
          <!-- <v-dialog v-model="dialogExcel">
            <v-card>
              <a class="ex-btn-s" :href="excel" download>
                {{ $t("PLACEHOLDERS.export_excel") }}
              </a>
              <v-card-actions>
                <v-btn class="modal_cancel_btn" @click="dialogExcel = false">{{ $t("BUTTONS.cancel") }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog> -->
          <!-- End:: excel Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
      <!-- ==== Start:: Totals Summary ==== -->
      <div class="table_content">
        <div class="table_title_wrapper">
          <div class="title_text_wrapper">
            <h5>{{ $t("PLACEHOLDERS.totals_summary") }}</h5>
          </div>
        </div>
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_packages_price") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_drivers_profit") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_distribution_points_profit") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.total_system_profit") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center all-stat">
                <td>{{ total_packages_price || 0 }}</td>
                <td>{{ total_drivers_profit || 0 }}</td>
                <td>{{ total_distribution_points_profit || 0 }}</td>
                <td>{{ total_system_profit || 0 }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!-- ==== End:: Totals Summary ==== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('PLACEHOLDERS.financial_reports')"
      :pdf-quality="2"
      pdf-format="a4"
      @progress="pdfDownloadBtnIsLoading = true"
      @hasGenerated="handlePdfGenerated"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("PLACEHOLDERS.financial_reports") }}
          </h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <template v-for="(header, index) in tableHeaders">
                    <th :key="header.value" v-if="index !== 0">
                      <span>{{ header.text }}</span>
                    </th>
                  </template>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in tableRows"
                  :key="row.id || `financial-row-${index}`"
                >
                  <td>{{ row.package_number || "-" }}</td>
                  <td>{{ row.driver_name || "-" }}</td>
                  <td>{{ row.dp_name || "-" }}</td>
                  <td>{{ row.delivery_fee || 0 }}</td>
                  <td>{{ row.driver_profit || 0 }}</td>
                  <td>{{ row.dp_profit || 0 }}</td>
                  <td>{{ row.system_profit || 0 }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- ==== Start:: Overall_statistics Addresses ==== -->

          <div class="table_content">
            <div class="table_title_wrapper">
              <div class="title_text_wrapper">
                <!-- <h5>{{ $t("PLACEHOLDERS.Overall_statistics") }}</h5> -->
              </div>
            </div>
            <v-simple-table class="stat-table mt-5">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.total_packages_price") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.total_drivers_profit") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.total_distribution_points_profit") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.total_system_profit") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template>
                    <tr class="text-center all-stat">
                      <td>{{ total_packages_price || 0 }}</td>
                      <td>{{ total_drivers_profit || 0 }}</td>
                      <td>{{ total_distribution_points_profit || 0 }}</td>
                      <td>{{ total_system_profit || 0 }}</td>
                    </tr>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <!-- ==== End:: Overall_statistics Addresses ==== -->
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";

export default {
  name: "AllFinancialReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    periodOptions() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.daily_period"),
          value: "daily",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.weekly_period"),
          value: "weekly",
        },
        {
          id: 3,
          name: this.$t("PLACEHOLDERS.monthly_period"),
          value: "monthly",
        },
      ];
    },
  },

  data() {
    return {
      total_packages_price: 0,
      total_drivers_profit: 0,
      total_distribution_points_profit: 0,
      total_system_profit: 0,

      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      pdfDownloadBtnIsLoading: false,
      excelDownloadBtnIsLoading: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        driver_name: null,
        dp_name: null,
        package_number: null,
        period: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.package_number"),
          value: "package_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.driver_name"),
          value: "driver_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.dp_name"),
          value: "dp_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.delivery_fee"),
          value: "delivery_fee",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.driver_profit"),
          value: "driver_profit",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.dp_profit"),
          value: "dp_profit",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.system_profit"),
          value: "system_profit",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.driver_name = null;
      this.filterOptions.dp_name = null;
      this.filterOptions.package_number = null;
      this.filterOptions.period = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    async setTableRows() {
      this.loading = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: "reports/financial",
          params: {
            page: this.paginations.current_page,
            per_page: this.paginations.items_per_page,
            driver_name: this.filterOptions.driver_name,
            dp_name: this.filterOptions.dp_name,
            package_number: this.filterOptions.package_number,
            period: this.filterOptions.period?.value,
            date: this.filterOptions.from_date,
          },
        });

        this.loading = false;
        this.tableRows = res.data.data.data;

        // Set totals
        this.total_packages_price = res.data.data.totals.total_packages_price;
        this.total_drivers_profit = res.data.data.totals.total_drivers_profit;
        this.total_distribution_points_profit =
          res.data.data.totals.total_distribution_points_profit;
        this.total_system_profit = res.data.data.totals.total_system_profit;

        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
      }
    },
    // End:: Set Table Rows

    // Start:: Handling Download Files
    async downloadExcel() {
      this.excelDownloadBtnIsLoading = true;

      try {
        const res = await this.$axios({
          method: "GET",
          url: "reports/financial",
          params: {
            format: "excel",
            driver_name: this.filterOptions.driver_name,
            dp_name: this.filterOptions.dp_name,
            package_number: this.filterOptions.package_number,
            period: this.filterOptions.period?.value,
            date: this.filterOptions.from_date,
          },
          responseType: "blob",
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${this.$t(
            "PLACEHOLDERS.financial_reports"
          )}_${new Date().getTime()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.excelDownloadBtnIsLoading = false;
        this.$message.success(this.$t("MESSAGES.downloadedSuccessfully"));
      } catch (error) {
        this.excelDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message);
        this.$message.error(this.$t("MESSAGES.downloadFailed"));
      }
    },

    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },

    handlePdfGenerated() {
      this.pdfDownloadBtnIsLoading = false;
      this.$message.success(this.$t("MESSAGES.generatedSuccessfully"));
    },

    // ==================== Start:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style lang="scss" scoped>
.ex-btn-s {
  color: black !important;
  font-size: 20px;
  font-weight: bold;
  text-decoration: underline !important;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat td {
  color: white;
  font-size: 21px !important;
  font-weight: 800;
}

.show_all_content_wrapper
  .v-data-table
  .v-data-table__wrapper
  table
  tbody
  tr:hover {
  background: var(--main_theme_clr) !important;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}

.stat-table {
  background: var(--theme_second_bg_clr);
}

.pdf_file_content {
  background-color: white;

  .file_title {
    color: #333;
  }
}

// Dark Theme Support
:root .dark_theme {
  .stat-table {
    background: var(--theme_second_bg_clr);

    thead th {
      color: var(--theme_text_clr);
      background-color: var(--theme_main_bg_clr);
    }

    tbody td {
      color: var(--theme_text_clr);
    }
  }

  .pdf_file_content {
    background-color: var(--theme_second_bg_clr);

    .file_title {
      color: var(--theme_text_clr);
    }
  }
}
</style>
